import type {
  CommerceComponent,
  PlatformFeeCollectionMode,
  PlatformFeePolicySource,
  PlatformFeeSnapshot,
} from "./billing.js";

export const commerceProviders = ["stripe"] as const;
export const stripeConnectAccountArchitectures = ["standard"] as const;
export const commerceReadinessStates = [
  "notConnected",
  "onboardingRequired",
  "pending",
  "restricted",
  "ready",
] as const;
export const commerceOnboardingStatuses = [
  "notStarted",
  "started",
  "detailsSubmitted",
  "restricted",
  "ready",
] as const;
export const commerceCapabilityStatuses = ["active", "inactive", "pending", "unknown"] as const;
export const donationTransactionStatuses = [
  "pending",
  "requiresAction",
  "processing",
  "confirmed",
  "failed",
  "cancelled",
] as const;
export const donationRefundStatuses = [
  "pending",
  "processing",
  "confirmed",
  "failed",
  "cancelled",
] as const;
export const donationRefundSummaryStatuses = [
  "none",
  "processing",
  "partiallyRefunded",
  "refunded",
  "failed",
] as const;
export const donationReceiptTypes = [
  "donation",
  "refund",
  "iftarSponsorship",
  "spaceReservation",
] as const;
export const financialLedgerEntryTypes = [
  "donation_organization_net",
  "donation_refund_organization_net",
  "iftar_sponsorship_organization_net",
  "space_reservation_organization_net",
] as const;
export const financialLedgerDirections = ["credit", "debit"] as const;
export const givingFundTypes = ["general", "zakat", "sadaqah", "custom"] as const;
export const givingFundStatuses = ["draft", "active", "archived"] as const;
export const givingFundVisibilities = ["connected", "private"] as const;
export const campaignStatuses = ["draft", "published", "completed", "archived"] as const;
export const campaignVisibilities = ["connected", "private"] as const;
export const donationDestinationTypes = ["general", "fund", "campaign"] as const;
export const commerceReconciliationStatuses = [
  "matched",
  "mismatch",
  "providerMissing",
  "shdeedMissing",
  "stale",
  "error",
] as const;
export const commerceReconciliationMismatchTypes = [
  "provider_payment_intent_missing",
  "provider_payment_intent_status_mismatch",
  "provider_amount_mismatch",
  "provider_currency_mismatch",
  "refund_amount_mismatch",
  "refund_status_mismatch",
  "refund_provider_reference_missing",
  "cross_organization_provider_reference",
  "provider_error",
] as const;

export type CommerceProvider = (typeof commerceProviders)[number];
export type StripeConnectAccountArchitecture = (typeof stripeConnectAccountArchitectures)[number];
export type CommerceReadinessState = (typeof commerceReadinessStates)[number];
export type CommerceOnboardingStatus = (typeof commerceOnboardingStatuses)[number];
export type CommerceCapabilityStatus = (typeof commerceCapabilityStatuses)[number];
export type DonationTransactionStatus = (typeof donationTransactionStatuses)[number];
export type DonationRefundStatus = (typeof donationRefundStatuses)[number];
export type DonationRefundSummaryStatus = (typeof donationRefundSummaryStatuses)[number];
export type DonationReceiptType = (typeof donationReceiptTypes)[number];
export type FinancialLedgerEntryType = (typeof financialLedgerEntryTypes)[number];
export type FinancialLedgerDirection = (typeof financialLedgerDirections)[number];
export type GivingFundType = (typeof givingFundTypes)[number];
export type GivingFundStatus = (typeof givingFundStatuses)[number];
export type GivingFundVisibility = (typeof givingFundVisibilities)[number];
export type CampaignStatus = (typeof campaignStatuses)[number];
export type CampaignVisibility = (typeof campaignVisibilities)[number];
export type DonationDestinationType = (typeof donationDestinationTypes)[number];
export type CommerceReconciliationStatus = (typeof commerceReconciliationStatuses)[number];
export type CommerceReconciliationMismatchType =
  (typeof commerceReconciliationMismatchTypes)[number];

export type CommerceRequirementsSummary = {
  currentlyDue: string[];
  disabledReason: string | null;
  eventuallyDue: string[];
  pastDue: string[];
  pendingVerification: string[];
};

export type CommerceCapabilitySummary = {
  cardPayments: CommerceCapabilityStatus;
  transfers: CommerceCapabilityStatus;
};

export type OrganizationCommerceConfiguration = {
  architecture: StripeConnectAccountArchitecture;
  capabilityStatus: CommerceCapabilitySummary;
  chargesEnabled: boolean;
  connectedAccountId: string | null;
  createdAt: unknown;
  detailsSubmitted: boolean;
  lastProviderSyncAt: unknown | null;
  onboardingCompletedAt: unknown | null;
  onboardingStartedAt: unknown | null;
  onboardingStatus: CommerceOnboardingStatus;
  organizationId: string;
  payoutsEnabled: boolean;
  provider: CommerceProvider;
  providerCountry: string | null;
  providerDefaultCurrency: string | null;
  readiness: CommerceReadinessState;
  requirements: CommerceRequirementsSummary;
  restrictedReasonCode: string | null;
  source: "experience_twenty_six";
  updatedAt: unknown;
};

export type CommerceMediaReference = {
  altText: string | null;
  altTextAr: string | null;
  storagePath: string | null;
};

export type GivingDestinationAggregate = {
  aggregateUpdatedAt: unknown | null;
  confirmedAmountMinor: number;
  confirmedDonationCount: number;
};

export type OrganizationGivingFund = GivingDestinationAggregate & {
  createdAt: unknown;
  createdBy: string;
  description: string;
  descriptionAr: string | null;
  designation: GivingFundType;
  displayOrder: number;
  fundId: string;
  image: CommerceMediaReference;
  name: string;
  nameAr: string | null;
  organizationId: string;
  source: "experience_twenty_eight";
  status: GivingFundStatus;
  type: GivingFundType;
  updatedAt: unknown;
  updatedBy: string;
  visibility: GivingFundVisibility;
};

export type OrganizationCampaign = GivingDestinationAggregate & {
  archivedAt: unknown | null;
  campaignId: string;
  completedAt: unknown | null;
  createdAt: unknown;
  createdBy: string;
  currency: "usd";
  description: string;
  descriptionAr: string | null;
  displayOrder: number;
  endsAt: unknown | null;
  fundId: string | null;
  goalAmountMinor: number | null;
  image: CommerceMediaReference;
  organizationId: string;
  publishedAt: unknown | null;
  source: "experience_twenty_eight";
  startsAt: unknown | null;
  status: CampaignStatus;
  title: string;
  titleAr: string | null;
  updatedAt: unknown;
  updatedBy: string;
  visibility: CampaignVisibility;
};

export type DonationDestinationSnapshot = {
  campaignId: string | null;
  campaignTitleArSnapshot: string | null;
  campaignTitleSnapshot: string | null;
  destinationType: DonationDestinationType;
  fundId: string | null;
  fundNameArSnapshot: string | null;
  fundNameSnapshot: string | null;
  fundTypeSnapshot: GivingFundType | null;
};

export type DonationTransaction = {
  amountMinor: number;
  baseAmountMinor?: number;
  cancelledAt: unknown | null;
  campaignId: string | null;
  campaignTitleArSnapshot: string | null;
  campaignTitleSnapshot: string | null;
  confirmedAt: unknown | null;
  createdAt: unknown;
  currency: "usd";
  destinationType: DonationDestinationType;
  donorDisplayNameSnapshot: string | null;
  failedAt: unknown | null;
  fundId: string | null;
  fundNameArSnapshot: string | null;
  fundNameSnapshot: string | null;
  fundTypeSnapshot: GivingFundType | null;
  grossAmountMinor: number;
  idempotencyKeyHash: string;
  organizationId: string;
  organizationNetMinor: number;
  payerTotalMinor?: number;
  personId: string;
  platformFeeCollectionMode?: PlatformFeeCollectionMode;
  platformFeePolicyId?: string | null;
  platformFeePolicySource?: PlatformFeePolicySource;
  platformFeePolicyVersion?: number | null;
  platformFeeMinor: number;
  platformFeeSnapshot?: PlatformFeeSnapshot;
  provider: CommerceProvider;
  providerChargeId: string | null;
  providerConnectedAccountId: string;
  providerPaymentIntentId: string | null;
  providerStatus: string;
  receiptId: string | null;
  refundedAmountMinor: number;
  refundPendingAmountMinor: number;
  refundStatus: DonationRefundSummaryStatus;
  remainingRefundableAmountMinor: number;
  source: "experience_twenty_seven" | "experience_twenty_eight";
  status: DonationTransactionStatus;
  commerceComponent?: Extract<CommerceComponent, "generalDonation" | "campaignDonation">;
  transactionId: string;
  transactionType: "donation";
  updatedAt: unknown;
};

export type DonationLedgerEntry = {
  amountMinor: number;
  createdAt: unknown;
  createdBy: "system:stripeConnectWebhook" | "system:stripeRefundWebhook";
  currency: "usd";
  direction: FinancialLedgerDirection;
  ledgerEntryId: string;
  organizationId: string;
  provider: CommerceProvider;
  providerPaymentIntentId: string;
  providerRefundId?: string | null;
  refundId?: string | null;
  source: "experience_twenty_seven" | "experience_twenty_nine";
  transactionId: string;
  type: FinancialLedgerEntryType;
};

export type DonationReceipt = {
  amountMinor: number;
  campaignId: string | null;
  campaignTitleArSnapshot: string | null;
  campaignTitleSnapshot: string | null;
  confirmedAt: unknown;
  createdAt: unknown;
  currency: "usd";
  destinationType: DonationDestinationType;
  donorDisplayNameSnapshot: string | null;
  fundId: string | null;
  fundNameArSnapshot: string | null;
  fundNameSnapshot: string | null;
  fundTypeSnapshot: GivingFundType | null;
  organizationId: string;
  personId: string;
  provider: CommerceProvider;
  providerPaymentIntentId: string;
  providerRefundId?: string | null;
  receiptId: string;
  receiptNumber: string;
  receiptType?: DonationReceiptType;
  refundId?: string | null;
  refundedAmountMinor?: number;
  refundStatus?: DonationRefundSummaryStatus;
  remainingAmountMinor?: number;
  source: "experience_twenty_seven" | "experience_twenty_nine";
  transactionId: string;
};

export type DonationRefund = {
  amountMinor: number;
  cancelledAt: unknown | null;
  confirmedAt: unknown | null;
  createdAt: unknown;
  currency: "usd";
  failedAt: unknown | null;
  failureCode: string | null;
  failureReason: string | null;
  idempotencyDocumentId: string;
  idempotencyKeyHash: string;
  ledgerEntryId: string | null;
  organizationId: string;
  organizationNetRefundMinor: number;
  originalTransactionId: string;
  personId: string;
  platformFeeRefundMinor: number;
  provider: CommerceProvider;
  providerChargeId: string | null;
  providerConnectedAccountId: string;
  providerPaymentIntentId: string;
  providerRefundId: string | null;
  providerStatus: string;
  reason: string;
  receiptId: string | null;
  refundId: string;
  requestedBy: string;
  requestedAt: unknown;
  source: "experience_twenty_nine";
  status: DonationRefundStatus;
  updatedAt: unknown;
};

export type DonationRefundIdempotencyRecord = {
  amountMinor: number;
  currency: "usd";
  idempotencyDocumentId: string;
  idempotencyKeyHash: string;
  organizationId: string;
  originalTransactionId: string;
  providerRefundId: string | null;
  refundId: string;
  requestedAmountMinor: number | null;
  requestedBy: string;
  source: "experience_twenty_nine";
  status: DonationRefundStatus;
};

export type CommerceReconciliationRun = {
  checkedAt: unknown;
  checkedTransactionCount: number;
  completedAt: unknown | null;
  createdAt: unknown;
  errorCode: string | null;
  mismatchCount: number;
  organizationId: string;
  requestedBy: string;
  runId: string;
  source: "experience_twenty_nine";
  status: CommerceReconciliationStatus;
  targetTransactionId: string | null;
  updatedAt: unknown;
};

export type CommerceReconciliationMismatch = {
  detectedAt: unknown;
  lastCheckedAt: unknown;
  mismatchId: string;
  organizationId: string;
  providerPaymentIntentId: string | null;
  providerRefundId: string | null;
  runId: string;
  safeDiagnosticCode: string;
  source: "experience_twenty_nine";
  status: "open" | "resolved";
  transactionId: string | null;
  type: CommerceReconciliationMismatchType;
};

export type StripeConnectedAccountState = {
  capabilities?: {
    card_payments?: string | null;
    transfers?: string | null;
  } | null;
  charges_enabled?: boolean | null;
  country?: string | null;
  default_currency?: string | null;
  details_submitted?: boolean | null;
  id: string;
  payouts_enabled?: boolean | null;
  requirements?: {
    currently_due?: string[] | null;
    disabled_reason?: string | null;
    eventually_due?: string[] | null;
    past_due?: string[] | null;
    pending_verification?: string[] | null;
  } | null;
};

export function organizationCommerceConfigDocumentPath(organizationId: string): string {
  return `organizations/${organizationId}/config/commerce`;
}

export function organizationCommerceProviderEventDocumentPath(
  organizationId: string,
  eventId: string,
): string {
  return `organizations/${organizationId}/commerceProviderEvents/${eventId}`;
}

export function organizationCommerceAuditEventDocumentPath(
  organizationId: string,
  eventId: string,
): string {
  return `organizations/${organizationId}/commerceAuditEvents/${eventId}`;
}

export function commerceConnectedAccountIndexDocumentPath(connectedAccountId: string): string {
  return `commerceConnectedAccounts/${connectedAccountId}`;
}

export function organizationTransactionDocumentPath(
  organizationId: string,
  transactionId: string,
): string {
  return `organizations/${organizationId}/transactions/${transactionId}`;
}

export function organizationDonationIdempotencyDocumentPath(
  organizationId: string,
  idempotencyDocumentId: string,
): string {
  return `organizations/${organizationId}/donationIdempotencyKeys/${idempotencyDocumentId}`;
}

export function organizationRefundDocumentPath(organizationId: string, refundId: string): string {
  return `organizations/${organizationId}/refunds/${refundId}`;
}

export function organizationRefundIdempotencyDocumentPath(
  organizationId: string,
  idempotencyDocumentId: string,
): string {
  return `organizations/${organizationId}/refundIdempotencyKeys/${idempotencyDocumentId}`;
}

export function organizationLedgerDocumentPath(
  organizationId: string,
  ledgerEntryId: string,
): string {
  return `organizations/${organizationId}/ledger/${ledgerEntryId}`;
}

export function organizationReceiptDocumentPath(organizationId: string, receiptId: string): string {
  return `organizations/${organizationId}/receipts/${receiptId}`;
}

export function organizationGivingFundDocumentPath(organizationId: string, fundId: string): string {
  return `organizations/${organizationId}/givingFunds/${fundId}`;
}

export function organizationCampaignDocumentPath(
  organizationId: string,
  campaignId: string,
): string {
  return `organizations/${organizationId}/campaigns/${campaignId}`;
}

export function organizationReconciliationRunDocumentPath(
  organizationId: string,
  runId: string,
): string {
  return `organizations/${organizationId}/reconciliationRuns/${runId}`;
}

export function organizationReconciliationMismatchDocumentPath(
  organizationId: string,
  runId: string,
  mismatchId: string,
): string {
  return `organizations/${organizationId}/reconciliationRuns/${runId}/mismatches/${mismatchId}`;
}

export function donationTransactionHasValidMinorUnitInvariant(
  transaction: Pick<
    DonationTransaction,
    "grossAmountMinor" | "organizationNetMinor" | "platformFeeMinor"
  >,
): boolean {
  return (
    transaction.grossAmountMinor === transaction.organizationNetMinor + transaction.platformFeeMinor
  );
}

export function isGivingFundType(value: unknown): value is GivingFundType {
  return typeof value === "string" && givingFundTypes.includes(value as GivingFundType);
}

export function isGivingFundStatus(value: unknown): value is GivingFundStatus {
  return typeof value === "string" && givingFundStatuses.includes(value as GivingFundStatus);
}

export function isCampaignStatus(value: unknown): value is CampaignStatus {
  return typeof value === "string" && campaignStatuses.includes(value as CampaignStatus);
}

export function isDonationDestinationType(value: unknown): value is DonationDestinationType {
  return (
    typeof value === "string" && donationDestinationTypes.includes(value as DonationDestinationType)
  );
}

export function isDonationRefundStatus(value: unknown): value is DonationRefundStatus {
  return (
    typeof value === "string" && donationRefundStatuses.includes(value as DonationRefundStatus)
  );
}

export function isDonationRefundSummaryStatus(
  value: unknown,
): value is DonationRefundSummaryStatus {
  return (
    typeof value === "string" &&
    donationRefundSummaryStatuses.includes(value as DonationRefundSummaryStatus)
  );
}

export function isCommerceReconciliationStatus(
  value: unknown,
): value is CommerceReconciliationStatus {
  return (
    typeof value === "string" &&
    commerceReconciliationStatuses.includes(value as CommerceReconciliationStatus)
  );
}

export function computeDonationRefundBalance(input: {
  amountMinor: number;
  refundedAmountMinor: number;
  refundPendingAmountMinor: number;
}): {
  protectedRefundAmountMinor: number;
  remainingRefundableAmountMinor: number;
  refundStatus: DonationRefundSummaryStatus;
} {
  const refundedAmountMinor = Math.max(input.refundedAmountMinor, 0);
  const refundPendingAmountMinor = Math.max(input.refundPendingAmountMinor, 0);
  const protectedRefundAmountMinor = refundedAmountMinor + refundPendingAmountMinor;
  const remainingRefundableAmountMinor = Math.max(
    input.amountMinor - protectedRefundAmountMinor,
    0,
  );

  let refundStatus: DonationRefundSummaryStatus = "none";
  if (refundPendingAmountMinor > 0) {
    refundStatus = "processing";
  } else if (refundedAmountMinor >= input.amountMinor && input.amountMinor > 0) {
    refundStatus = "refunded";
  } else if (refundedAmountMinor > 0) {
    refundStatus = "partiallyRefunded";
  }

  return {
    protectedRefundAmountMinor,
    remainingRefundableAmountMinor,
    refundStatus,
  };
}

export function isValidCampaignGoalAmountMinor(value: unknown): value is number | null {
  return (
    value === null ||
    (typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 100_000_000)
  );
}

export function computeGivingDestinationProgress(input: {
  confirmedAmountMinor: number;
  goalAmountMinor: number | null;
}): { percentComplete: number | null; remainingAmountMinor: number | null } {
  if (input.goalAmountMinor === null || input.goalAmountMinor <= 0) {
    return {
      percentComplete: null,
      remainingAmountMinor: null,
    };
  }

  return {
    percentComplete: Math.floor((input.confirmedAmountMinor * 100) / input.goalAmountMinor),
    remainingAmountMinor: Math.max(input.goalAmountMinor - input.confirmedAmountMinor, 0),
  };
}

export function normalizeStripeConnectedAccountState(
  account: StripeConnectedAccountState | undefined,
): Pick<
  OrganizationCommerceConfiguration,
  | "capabilityStatus"
  | "chargesEnabled"
  | "detailsSubmitted"
  | "onboardingStatus"
  | "payoutsEnabled"
  | "providerCountry"
  | "providerDefaultCurrency"
  | "readiness"
  | "requirements"
  | "restrictedReasonCode"
> {
  if (!account) {
    return {
      capabilityStatus: {
        cardPayments: "unknown",
        transfers: "unknown",
      },
      chargesEnabled: false,
      detailsSubmitted: false,
      onboardingStatus: "notStarted",
      payoutsEnabled: false,
      providerCountry: null,
      providerDefaultCurrency: null,
      readiness: "notConnected",
      requirements: emptyCommerceRequirementsSummary(),
      restrictedReasonCode: null,
    };
  }

  const requirements: CommerceRequirementsSummary = {
    currentlyDue: safeStringArray(account.requirements?.currently_due),
    disabledReason: safeOptionalString(account.requirements?.disabled_reason),
    eventuallyDue: safeStringArray(account.requirements?.eventually_due),
    pastDue: safeStringArray(account.requirements?.past_due),
    pendingVerification: safeStringArray(account.requirements?.pending_verification),
  };
  const capabilityStatus: CommerceCapabilitySummary = {
    cardPayments: normalizeCapabilityStatus(account.capabilities?.card_payments),
    transfers: normalizeCapabilityStatus(account.capabilities?.transfers),
  };
  const chargesEnabled = account.charges_enabled === true;
  const payoutsEnabled = account.payouts_enabled === true;
  const detailsSubmitted = account.details_submitted === true;
  const readiness = commerceReadinessFromProviderState({
    capabilityStatus,
    chargesEnabled,
    detailsSubmitted,
    payoutsEnabled,
    requirements,
  });

  return {
    capabilityStatus,
    chargesEnabled,
    detailsSubmitted,
    onboardingStatus: onboardingStatusFromReadiness(readiness, detailsSubmitted),
    payoutsEnabled,
    providerCountry: safeOptionalString(account.country),
    providerDefaultCurrency: safeOptionalString(account.default_currency),
    readiness,
    requirements,
    restrictedReasonCode: requirements.disabledReason,
  };
}

export function emptyCommerceRequirementsSummary(): CommerceRequirementsSummary {
  return {
    currentlyDue: [],
    disabledReason: null,
    eventuallyDue: [],
    pastDue: [],
    pendingVerification: [],
  };
}

function commerceReadinessFromProviderState(input: {
  capabilityStatus: CommerceCapabilitySummary;
  chargesEnabled: boolean;
  detailsSubmitted: boolean;
  payoutsEnabled: boolean;
  requirements: CommerceRequirementsSummary;
}): CommerceReadinessState {
  if (input.requirements.disabledReason || input.requirements.pastDue.length > 0) {
    return "restricted";
  }

  if (!input.detailsSubmitted || input.requirements.currentlyDue.length > 0) {
    return "onboardingRequired";
  }

  if (
    input.chargesEnabled &&
    input.payoutsEnabled &&
    input.capabilityStatus.cardPayments === "active" &&
    input.capabilityStatus.transfers === "active"
  ) {
    return "ready";
  }

  return "pending";
}

function onboardingStatusFromReadiness(
  readiness: CommerceReadinessState,
  detailsSubmitted: boolean,
): CommerceOnboardingStatus {
  switch (readiness) {
    case "notConnected":
      return "notStarted";
    case "onboardingRequired":
      return detailsSubmitted ? "started" : "started";
    case "pending":
      return "detailsSubmitted";
    case "restricted":
      return "restricted";
    case "ready":
      return "ready";
  }
}

function normalizeCapabilityStatus(value: unknown): CommerceCapabilityStatus {
  if (value === "active" || value === "inactive" || value === "pending") {
    return value;
  }

  return "unknown";
}

function safeStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

function safeOptionalString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}
